import React, { useContext, useEffect, useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import cx from 'classnames';
import FocusTrap from 'focus-trap-react';
import CloseS1 from 'plaid-threads/Icons/CloseS1';

import globalStyles from 'src/components/docs/DocsBeta/index.module.scss';
import IconButton from 'src/components/Inputs/IconButton/IconButton';
import Image from 'src/components/Image';
import Context from 'src/contexts/docs';
import ChatDialog from 'src/components/docs/Bill';
import { COREX_PRIMER_QUESTIONS } from 'src/components/docs/Bill/constants';
import { CHATBOT_EVENTS } from 'src/lib/trackingEvents';
import { useTrack } from 'src/contexts/AnalyticsContext';

import styles from 'src/components/docs/SearchParent/index.module.scss';
import SearchDialog from './SearchDialog';

enum SearchType {
  SEARCH = 'search',
  CHAT = 'chat',
}

const COREX_INTRO_TEXT =
  "Hi! I'm Bill! You can ask me all about Core Exchange. Try asking questions like:";

const SearchParent: React.FC<{ showFinn?: boolean }> = ({
  showFinn = false,
}) => {
  const { showSearch, dispatch } = useContext(Context);
  const [searchType, setSearchType] = useState(SearchType.SEARCH);
  const router = useRouter();
  const track = useTrack();

  const closeSearch = useCallback(() => {
    dispatch({
      type: 'SET_SEARCH_VISIBILITY',
      payload: { visibility: false, openSearchClicked: false },
    });
  }, [dispatch]);

  // Close search dialog on any route change (including hash changes)
  useEffect(() => {
    const handleRouteChange = () => {
      if (showSearch) {
        closeSearch();
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);
    router.events.on('hashChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      router.events.off('hashChangeStart', handleRouteChange);
    };
  }, [router.events, showSearch, closeSearch]);

  // If the url contains showChat=true (or a conversation id), open Bill by default
  useEffect(() => {
    if (!showFinn) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (
      params.get('showChat') === 'true' ||
      params.get('conversation') != null
    ) {
      dispatch({
        type: 'SET_SEARCH_VISIBILITY',
        payload: { visibility: true, openSearchClicked: true },
      });
      setSearchType(SearchType.CHAT);
    }
  }, [showFinn, dispatch]);

  const trackTabSelected = useCallback(() => {
    track({
      type: CHATBOT_EVENTS.CHAT_SEARCH_TAB_SELECTED,
      payload: { tab: searchType },
    });
  }, [track, searchType]);

  useEffect(() => {
    if (!showSearch) {
      return;
    }

    if (searchType === SearchType.SEARCH) {
      const el = document.querySelector('#search-input');
      if (el instanceof HTMLInputElement) {
        el.focus();
      }
    } else if (searchType === SearchType.CHAT) {
      const el = document.querySelector('#finn-input-div input');
      if (el instanceof HTMLInputElement) {
        el.focus();
      }
    }

    // Tabs only exist when Bill is enabled, so only the tab analytics is gated.
    if (showFinn) {
      trackTabSelected();
    }
  }, [searchType, showSearch, showFinn, trackTabSelected]);

  return (
    <>
      <div className={!showSearch ? styles.hidden : ''}>
        <div
          className={cx(styles.backdrop, showSearch ? styles.show : '')}
          onClick={closeSearch}
          onKeyUp={closeSearch}
          role='button'
          tabIndex={0}
        />

        <FocusTrap
          active={showSearch}
          focusTrapOptions={{
            escapeDeactivates: false,
            initialFocus: false,
            allowOutsideClick: true,
            returnFocusOnDeactivate: true,
          }}
        >
          <div className={cx(globalStyles.docs, styles.parentModalContainer)}>
            <div className={styles.offsetClose}>
              <IconButton
                accessibilityLabel='Close search modal'
                icon={<CloseS1 />}
                onClick={closeSearch}
              />
            </div>
            {showFinn && (
              <div className={styles.topBar}>
                <span
                  className={cx(
                    styles.barLabel,
                    searchType === SearchType.SEARCH && styles.selected,
                  )}
                >
                  <button
                    className={styles.barButton}
                    onClick={() => setSearchType(SearchType.SEARCH)}
                  >
                    Search the docs
                  </button>
                </span>
                <span
                  className={cx(
                    styles.barLabel,
                    searchType === SearchType.CHAT && styles.selected,
                  )}
                >
                  <button
                    className={styles.barButton}
                    onClick={() => setSearchType(SearchType.CHAT)}
                  >
                    Ask Bill!
                  </button>
                  <Image
                    src='/assets/img/docs/bill-happy.png'
                    alt='Ask Bill!'
                    isLazy={false}
                    className={styles.billIcon}
                    width={25}
                    height={25}
                  />
                </span>
              </div>
            )}
            <SearchDialog
              isVisible={showSearch && searchType === SearchType.SEARCH}
            />
            {showFinn && (
              <ChatDialog
                isVisible={showSearch && searchType === SearchType.CHAT}
                introText={COREX_INTRO_TEXT}
                primerQuestions={COREX_PRIMER_QUESTIONS}
                section='corex'
                showCodingToolkitPlug={false}
              />
            )}
          </div>
        </FocusTrap>
      </div>
    </>
  );
};

SearchParent.displayName = 'CoreExchangeSearchParent';
export default SearchParent;
